import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../config/firebase";
import { collectionGroup, getDocs, Timestamp } from "firebase/firestore";
import "./orders.css";

const OrdersPage = () => {
    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const fetchAllOrders = async () => {
            try {
                setLoading(true);  
                setError("");

                const ordersRef = collectionGroup(db, "orders");
                const ordersSnapshot = await getDocs(ordersRef);

                if (ordersSnapshot.empty) {
                    console.log("No orders found in any user document");
                    setOrders([]);
                    return;
                }

                console.log(`Found ${ordersSnapshot.size} orders across all users`);

                const allOrders = ordersSnapshot.docs.map(doc => {
                    const fullPath = doc.ref.path;
                    const pathParts = fullPath.split('/');
                    const userId = pathParts[1];

                    let orderData = { id: doc.id, ...doc.data() };

                    // Handle Timestamp conversion if needed
                    if (orderData.createdAt instanceof Timestamp) {
                        orderData.createdAt = orderData.createdAt.toDate();
                    }

                    // Parse orderDate string into a Date object
                    if (orderData.orderDetails?.orderDate) {
                        try {
                            const [datePart, timePart] = orderData.orderDetails.orderDate.split(" ");
                            const [day, month, year] = datePart.split("/").map(Number);
                            const [hours, minutes, seconds] = timePart.split(":").map(Number);
                            orderData.orderDateObj = new Date(year, month - 1, day, hours, minutes, seconds);
                        } catch (e) {
                            console.warn(`Failed to parse date for order ${orderData.id}:`, e);
                            orderData.orderDateObj = new Date(0);
                        }
                    } else {
                        orderData.orderDateObj = new Date(0);
                    }

                    return { ...orderData, userId };
                });

                // Sort orders by date (newest first)
                allOrders.sort((a, b) => b.orderDateObj.getTime() - a.orderDateObj.getTime());

                setOrders(allOrders);
            } catch (error) {
                console.error("Error fetching orders:", error);
                if (error.code === 'failed-precondition' && error.message.includes('index')) {
                    setError("Firestore index required. Check console for the link to create it.");
                    console.log("Please click the link in the error message above to create the required index.");
                } else {
                    setError(`Failed to fetch orders: ${error.message}`);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchAllOrders();
    }, []);

    const filteredOrders = orders.filter(order =>
        order.customer?.name?.toLowerCase().includes(search.toLowerCase()) ||
        order.id?.toLowerCase().includes(search.toLowerCase()) ||
        order.orderStatus?.toLowerCase().includes(search.toLowerCase())
    );

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            if (dateString.includes('/')) {
                return dateString.split(' ')[0];
            }
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    const formatPrice = (amount) => {
        if (!amount && amount !== 0) return "₹ 0.00";
        const numericValue = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.]/g, "")) : amount;
        return isNaN(numericValue) ? "₹ 0.00" : `₹ ${numericValue.toFixed(2)}`;
    };

    return (
        <div className="orders-container">
            <h2>All Orders</h2>

            <button
                onClick={() => window.location.href = '/home'}
                className="dashboard-button1"
            >
                Home
            </button>

            <input
                type="text"
                placeholder="Search orders by name, ID, or status..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
            />
            {loading ? (
                <p>Loading orders...</p>
            ) : error ? (
                <p className="error">{error}</p>
            ) : filteredOrders.length > 0 ? (
                <div className="orders-list">
                    {filteredOrders.map((order) => (
                        <div
                            key={order.id}
                            className="order-item"
                            onClick={() => navigate(`/orders/${order.userId}/${order.id}`)}
                        >
                            <h3>{order.customer?.name || "Unknown Customer"}</h3>
                            <p><strong>Order ID:</strong> {order.id}</p>
                            <p><strong>Status:</strong> {order.orderStatus || "N/A"}</p>
                            <p><strong>Ordered On:</strong> {formatDate(order.orderDetails?.orderDate)}</p>
                            <p><strong>Total:</strong> {formatPrice(order.totals?.total)}</p>
                        </div>
                    ))}
                </div>
            ) : (
                <p>No orders found. {search ? "Try clearing your search filter." : ""}</p>
            )}
        </div>
    );
};

export default OrdersPage;
